// ENG-6412 — host config capture (allowlist-only).
//
// Builds the capturable slice of a host's effective configuration for the
// host_config_snapshots change-log: resolved feature-flag values + non-secret
// env gates + a content hash. The hard security invariant is that ONLY
// allowlisted, non-secret env names are ever read — never a blanket sweep of
// process.env — so secrets (API keys, tokens, service-role keys) cannot reach
// the snapshot by construction.
import { createHash } from 'node:crypto';
import { FLAG_REGISTRY } from '../feature-flags/registry.js';
import type { EvaluatedFlags, FlagValue } from '../feature-flags/types.js';

/**
 * Explicit allowlist of known non-secret operator gates that are NOT (yet) in
 * the feature-flag registry. Registry gates contribute their `envVar`
 * automatically (see listCapturableEnvGateNames), so this list only covers the
 * env gates ADR-0022 hasn't migrated into the registry. Every entry must be a
 * boolean/mode gate, never a secret — the SECRET_NAME_PATTERN filter below is
 * the structural backstop, and the secret-exclusion test pins it.
 */
export const NON_SECRET_ENV_GATES: readonly string[] = [
  'AGT_MEMORY_EXTRACTION_ENABLED',
  'AGT_CHANNEL_REPLAY_ENABLED',
  'AGT_DISABLE_MSTEAMS_HOST_ROUTES',
  'AGT_DEPLOY_CRONS',
  'AGT_CONV_EVAL_BACKEND',
  'AGT_CLI_RELEASE_CHANNEL',
];

/**
 * Names that must NEVER be captured. Used both as a hard filter when building
 * the allowlist (defense in depth — a secret-shaped name mistakenly added to
 * NON_SECRET_ENV_GATES or to a registry envVar is dropped) and asserted in the
 * secret-exclusion test.
 */
export const SECRET_NAME_PATTERN =
  /(KEY|SECRET|TOKEN|PASSWORD|PASSWD|CREDENTIAL|PRIVATE|SIGNING|WEBHOOK)/i;

/**
 * The exact set of env-var names eligible for capture: every registry flag's
 * legacy `envVar` plus the explicit non-secret gate list, with any secret-shaped
 * name filtered out. Sorted + de-duped for a stable capture order.
 */
export function listCapturableEnvGateNames(): string[] {
  const fromRegistry = FLAG_REGISTRY.map((f) => f.envVar).filter(
    (v): v is string => typeof v === 'string' && v.length > 0,
  );
  return [...new Set([...fromRegistry, ...NON_SECRET_ENV_GATES])]
    .filter((name) => !SECRET_NAME_PATTERN.test(name))
    .sort();
}

/**
 * Read the values the host has set for the allowlisted gates. Only allowlisted
 * names are touched — an env var not on the list is never read, so this can't
 * leak an unrelated secret. Unset gates are omitted (vs captured as empty) so
 * the snapshot reflects "what the operator actually configured". Values are
 * length-capped defensively.
 */
export function collectEnvGates(
  env: Record<string, string | undefined>,
): Record<string, string> {
  const out: Record<string, string> = {};
  for (const name of listCapturableEnvGateNames()) {
    const value = env[name];
    if (typeof value === 'string' && value.length > 0) {
      out[name] = value.length > 128 ? value.slice(0, 128) : value;
    }
  }
  return out;
}

/** The content that defines a config snapshot (and its content hash). */
export interface HostConfigSnapshotContent {
  /** Resolved registry flag values for the host's org. */
  featureFlags: EvaluatedFlags;
  /** Allowlisted non-secret env gates the host has set. */
  envGates: Record<string, string>;
  agtCliVersion: string | null;
  frameworkVersion: string | null;
  flagsSchemaVersion: string | null;
}

function sortedEntries(obj: Record<string, unknown>): Record<string, unknown> {
  return Object.fromEntries(
    Object.keys(obj)
      .sort()
      .map((k) => [k, obj[k]]),
  );
}

/**
 * Canonical, key-order-independent serialization of the config content — the
 * preimage for the content hash. Two hosts with the same effective config
 * produce identical bytes here regardless of insertion order.
 */
function canonicalize(content: HostConfigSnapshotContent): string {
  return JSON.stringify({
    featureFlags: sortedEntries(content.featureFlags),
    envGates: sortedEntries(content.envGates),
    agtCliVersion: content.agtCliVersion ?? null,
    frameworkVersion: content.frameworkVersion ?? null,
    flagsSchemaVersion: content.flagsSchemaVersion ?? null,
  });
}

/**
 * Content-addressed config hash (`cfg1:<32 hex>`). Deterministic over the
 * config content, so identical config ⇒ identical hash across hosts and over
 * time. This is the join key alerts/audit rows carry to recover their config.
 */
export function computeConfigHash(content: HostConfigSnapshotContent): string {
  // 128-bit prefix (32 hex) of SHA-256. The join logic assumes identical hashes
  // mean identical content, so use enough bits that a collision is negligible
  // rather than rely on bounded config cardinality. Still well under the 64-char
  // X-Config-Hash header cap.
  const digest = createHash('sha256').update(canonicalize(content)).digest('hex');
  return `cfg1:${digest.slice(0, 32)}`;
}

/**
 * The keys that changed between a host's prior snapshot and the next, namespaced
 * by kind (`flag:<key>`, `gate:<NAME>`, `version:<which>`). Empty for a host's
 * first snapshot. Drives the "which change caused the spike" query against
 * host_config_snapshots.changed_keys.
 */
export function diffConfigKeys(
  prev: HostConfigSnapshotContent | null,
  next: HostConfigSnapshotContent,
): string[] {
  if (!prev) return [];
  const changed: string[] = [];
  const differs = (a: unknown, b: unknown) => JSON.stringify(a) !== JSON.stringify(b);
  const union = (...objs: Record<string, unknown>[]) =>
    new Set(objs.flatMap((o) => Object.keys(o)));

  for (const k of union(prev.featureFlags, next.featureFlags)) {
    if (differs(prev.featureFlags[k], next.featureFlags[k])) changed.push(`flag:${k}`);
  }
  for (const k of union(prev.envGates, next.envGates)) {
    if (differs(prev.envGates[k], next.envGates[k])) changed.push(`gate:${k}`);
  }
  if (differs(prev.agtCliVersion, next.agtCliVersion)) changed.push('version:agt_cli');
  if (differs(prev.frameworkVersion, next.frameworkVersion)) changed.push('version:framework');
  if (differs(prev.flagsSchemaVersion, next.flagsSchemaVersion)) {
    changed.push('version:flags_schema');
  }
  return changed.sort();
}

export type { FlagValue };
